import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Calendar, MapPin, User, Clock, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, isToday, isTomorrow, isThisWeek, parseISO } from 'date-fns';

// Mock data for simulation
const MOCK_BOOKINGS = [
  { id: '1', course_name: 'First Aid Training', provider_name: 'SafetyFirst Ltd', start_date: new Date().toISOString(), location: 'Training Room A', attendee_count: 5 },
  { id: '2', course_name: 'Fire Safety Awareness', provider_name: 'FireSafe Training', start_date: new Date(Date.now() + 86400000).toISOString(), location: 'Main Office', attendee_count: 12 },
  { id: '3', course_name: 'Manual Handling', provider_name: 'Health & Safety Pro', start_date: new Date(Date.now() + 172800000).toISOString(), location: 'Site Alpha', attendee_count: 8 },
  { id: '4', course_name: 'COSHH Training', provider_name: 'ChemSafe Ltd', start_date: new Date(Date.now() + 604800000).toISOString(), location: 'Online', attendee_count: 15 },
];

const getDateLabel = (dateStr) => {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  if (isThisWeek(date)) return format(date, 'EEEE');
  return format(date, 'MMM d');
};

const getDateColor = (dateStr) => {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'bg-green-100 text-green-800 border-green-200';
  if (isTomorrow(date)) return 'bg-blue-100 text-blue-800 border-blue-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

export function BookingsOverviewTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setBookings(MOCK_BOOKINGS);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadBookings = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];

        const { data, error } = await supabase
          .from('training_bookings')
          .select(`
            id,
            course_name,
            provider_name,
            start_date,
            location,
            status
          `)
          .eq('company_id', company.id)
          .gte('start_date', today)
          .in('status', ['confirmed', 'pending'])
          .order('start_date', { ascending: true })
          .limit(10);

        if (error) throw error;

        // Get attendee counts
        const bookingsWithCounts = await Promise.all((data || []).map(async (booking) => {
          const { count } = await supabase
            .from('training_booking_attendees')
            .select('*', { count: 'exact', head: true })
            .eq('booking_id', booking.id);
          return { ...booking, attendee_count: count || 0 };
        }));

        setBookings(bookingsWithCounts);
      } catch (error) {
        console.error('Failed to load bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <Calendar className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No upcoming bookings</p>
        <p className="text-xs text-gray-500 mt-1">Schedule training sessions</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-500 text-white">{bookings.length}</Badge>
          <span className="text-xs text-gray-600">upcoming</span>
        </div>
      </div>

      {/* Bookings list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              onClick={() => navigate(`/bookings/${booking.id}`)}
              className="p-2 rounded-lg border bg-white hover:shadow-md cursor-pointer transition-all"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {booking.course_name}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate">
                    {booking.provider_name}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge className={cn("text-[10px] px-1.5 py-0", getDateColor(booking.start_date))}>
                      {getDateLabel(booking.start_date)}
                    </Badge>
                    {booking.location && (
                      <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                        <MapPin className="h-2.5 w-2.5" />
                        {booking.location}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                      <User className="h-2.5 w-2.5" />
                      {booking.attendee_count}
                    </span>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400 shrink-0" />
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Footer */}
      <button
        onClick={() => navigate('/bookings')}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors shrink-0"
      >
        <Calendar className="h-3.5 w-3.5" />
        <span>View All Bookings</span>
      </button>
    </div>
  );
}
